/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClientMsal } from './lib/client';
import { HttpClientConfigurator, } from './configurator';
import { HttpClientProvider } from './provider';
/**
 * Default HTTP module definition for Fusion Framework applications.
 *
 * The module uses `HttpClientMsal` as the default client implementation and,
 * when the auth module is available, installs a request handler that can acquire
 * bearer tokens for scoped requests.
 */
export const module = {
    name: 'http',
    /**
     * Configures the HTTP module with MSAL authentication.
     *
     * This function creates a new `HttpClientConfigurator` instance using the `HttpClientMsal` class.
     * The `HttpClientConfigurator` is responsible for configuring the HTTP client with the necessary options,
     * such as the base URL, request headers, and other settings.
     *
     * @returns A new `HttpClientConfigurator` instance configured for MSAL authentication.
     */
    configure: () => new HttpClientConfigurator(HttpClientMsal),
    /**
     * Initializes the HTTP client provider with MSAL authentication.
     *
     * This function is responsible for setting up the default HTTP request handler
     * to acquire an access token from MSAL and attach it to the request headers
     * when the request includes scopes.
     *
     * @param config - The module configuration.
     * @param hasModule - A function to check if a module is available.
     * @param requireInstance - A function to get an instance of a module.
     * @returns A promise that resolves to the HTTP client provider.
     */
    initialize: async ({ config, hasModule, requireInstance, }) => {
        const httpProvider = new HttpClientProvider(config);
        // wire up an MSAL bearer-token handler only when the auth module is registered
        if (hasModule('auth')) {
            const authProvider = await requireInstance('auth');
            httpProvider.defaultHttpRequestHandler.set('MSAL', async (request) => {
                const { scopes = [] } = request;
                // only attempt to acquire a token when the request actually declares scopes
                if (scopes.length) {
                    /** TODO(#5143): should be try catch, check caller for handling */
                    const accessToken = await authProvider.acquireAccessToken({
                        request: { scopes },
                    });
                    // without a token there's nothing to attach, fall through to the default request
                    if (accessToken) {
                        const headers = new Headers(request.headers);
                        headers.set('Authorization', `Bearer ${accessToken}`);
                        return { ...request, headers };
                    }
                }
            });
        }
        return httpProvider;
    },
};
/**
 * Creates a module configurator for the HTTP module.
 *
 * Use this when you need lower-level access to the module configuration callback,
 * for example when registering several named clients in one setup step.
 *
 * @param configure - The callback that receives the HTTP module configuration.
 * @returns A module configurator for the HTTP module.
 */
export const configureHttp = (configure) => ({
    module,
    configure,
});
/**
 * Creates a module configurator that registers one named HTTP client.
 *
 * This is the convenience API for the common case where a setup step only needs to
 * register one client with `baseUri`, `defaultScopes`, handlers, or a custom constructor.
 *
 * @param name - The client key used later with `createClient(name)`.
 * @param args - The named client configuration.
 * @returns A module configurator that registers the named client.
 */
export const configureHttpClient = (name, args) => ({
    module,
    configure: (config) => {
        config.configureClient(name, args);
    },
});
export default module;
//# sourceMappingURL=module.js.map